/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselSlider } from '@/components/blocks/Carousel';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { RenderContent } from '@/components/blocks/RenderContent';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { useDeviceType } from '@/data/deviceDetection';
import { ID } from '@/data/types/Basic';
import { CarouselOptions } from '@/data/types/Carousel';
import { WidgetProperties } from '@/data/types/Slot';
import { getCarouselProperties } from '@/utils/getCarouselProperties';
import { renderContent } from '@/utils/renderContent';
import { Box, Theme, useMediaQuery } from '@mui/material';
import React, { FC, useEffect, useId, useMemo, useState } from 'react';
import { ProcessedContent } from '@/data/types/Marketing';
import { getVisibleSlidesLength } from '@/utils/carouselSlidesConfig';

export const ContentRecommendationSingle: FC<{ content: ProcessedContent }> = ({ content }) => {
	const { onContentClick } = useContentEvents();

	return (
		<RenderContent
			key={`${content.id}${content.contentId}`}
			content={content}
			onClick={onContentClick(content)}
		/>
	);
};

export const ContentRecommendationMultiple: FC<{
	contents: ProcessedContent[];
	properties: WidgetProperties;
}> = ({ contents, properties }) => {
	const { onContentClick } = useContentEvents();

	const { isMobile: smMatch } = useDeviceType();
	const lgMatch = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
	const mdMatch = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
	const containerId = `el-${useId().replace(/:/g, "_")}`;
	const [isMobileHalfWidth, setMobileHalfWidth] = useState(false);
	useEffect(() => {
		if (!smMatch || isMobileHalfWidth) return;
		// This is to handle special scenario in IntertoysActiesAndFolderLayout3_rv layout
		// where a grid item has 9/12 width in desktop
		// and 6/12 in mobile (general case would be 12/12 in mobile)
		//xs={6} sm={3}
		const parentClasses = document.querySelector(`#${containerId}`)?.parentElement?.parentElement?.classList;
		if (parentClasses?.contains("MuiGrid-grid-sm-6")) setMobileHalfWidth(true);
	}, [containerId, smMatch, isMobileHalfWidth]);
	const { carouselContainerWidth = "100" } = properties;
	const isNotFullWidth = carouselContainerWidth !=="100";
	
	const visibleSlideLimit = useMemo(() => {
		if (smMatch) return isNotFullWidth && !isMobileHalfWidth ? 1 : 2.1;
		if (mdMatch || lgMatch) return isNotFullWidth ? getVisibleSlidesLength(carouselContainerWidth, 3, "md") : 3;
		return isNotFullWidth ? getVisibleSlidesLength(carouselContainerWidth, 4) : 4;
	}, [isNotFullWidth, smMatch, mdMatch, lgMatch, carouselContainerWidth, isMobileHalfWidth]);

	const defaultProps: CarouselOptions = useMemo(
		() => ({
			naturalSlideWidth: 600,
			naturalSlideHeight: 400,
			visibleSlides: Math.min(contents.length, visibleSlideLimit),
		}),
		[contents.length, visibleSlideLimit]
	);

	const slides = useMemo(
		() => contents.map((c) => renderContent(c, onContentClick(c))).filter(Boolean),
		[contents, onContentClick]
	);

	const carouselProps = useMemo(
		() => getCarouselProperties(properties, defaultProps),
		[defaultProps, properties]
	);

	return (
		<Box id={containerId} className={isNotFullWidth ? 'slider-not-full-width':''}>
			<CarouselSlider
				slides={slides}
				carouselProps={carouselProps}
				hideNavButtons={!isNotFullWidth && contents.length <= (defaultProps.visibleSlides as number)}
			></CarouselSlider>
		</Box>
	);
};

export const ContentRecommendation: FC<{ id: ID; properties?: WidgetProperties }> = ({
	id: _id,
	properties = {},
}) => {
	const { emsName = '' } = properties;
	const { data, loading, title } = useContentRecommendation(emsName);
	const { onContentClick } = useContentEvents();
	const contents = useMemo(() => data ?? [], [data]);

	return loading ? (
		<ProgressIndicator padding={0} />
	) : (
		// eslint-disable-next-line react/forbid-component-props,react/forbid-elements
		<div className="content-recomnedation-widget" id={_id.toString()}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
				/>
			))}

			{contents.length === 1 ? <ContentRecommendationSingle content={contents[0]} /> : null}
			{contents.length > 1 ? (
				<ContentRecommendationMultiple contents={contents} properties={properties} />
			) : null}
		</div>
	);
};
